<template>
    <!-- keydown, and every Enter handler in the app has to match it.
         A dialog closed with the keyboard leaks the other half of the press:
         Vuetify's VDialog returns focus to whatever was focused before it opened
         the moment it closes, so the KEYUP lands on the field the user had been
         typing in. When that field listened on keyup -- as every search and
         lookup in this app once did -- one press of Enter dismissed the message
         and immediately re-ran the thing that raised it, putting the same dialog
         straight back on screen. Only the mouse could escape it, because a click
         has no keyup.
         This listener is not the only thing that closes on keydown, so moving it
         would not have helped: the focused OK button is a native <button>, and a
         native button fires its click on Enter keydown. Fixing it means nothing
         in the app listening for the keyup at all, which check-view-chrome
         asserts. -->
    <v-dialog v-model="dialog" :max-width="options.width" @keydown.enter="handleEnter">
      <v-card>
        <v-toolbar dark :color="options.color">
          <v-toolbar-title>{{ title }}</v-toolbar-title>
        </v-toolbar>
        <!-- Multi-line message support -->
        <v-card-text class="pa-4">
            <template v-if="Array.isArray(message)">
            <div v-for="(line, index) in message" :key="index" class="message-line">
                <template v-if="line === ''"><br></template>
                <template v-else>{{ line }}</template>
            </div>
            </template>
            <template v-else-if="message">
            {{ message }}
            </template>
            <slot v-else></slot> <!-- Fallback to slot content -->
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <!-- Show "OK" only for alerts -->
          <v-btn ref="okButton" v-if="options.type === 'alert'" color="primary" @click="close">
            OK
          </v-btn>
          <!-- Show Yes/No for confirms -->
          <template v-else>
            <v-btn color="grey" text @click="close(false)">ยกเลิก</v-btn>
            <v-btn ref="confirmButton" color="primary" text @click="close(true)">ยืนยัน</v-btn>
          </template>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </template>
  
  <script>
  /**
   * Vuetify Confirm Dialog component
   *
   * Insert component where you want to use it:
   * <confirm ref="confirm"></confirm>
   *
   * Call it:
   * this.$refs.confirm.open('Delete', 'Are you sure?', { color: 'red' }).then((confirm) => {})
   * Or use await:
   * if (await this.$refs.confirm.open('Delete', 'Are you sure?', { color: 'red' })) {
   *   // yes
   * }
   * else {
   *   // cancel
   * }
   *
   * Alternatively you can place it in main App component and access it globally via this.$root.$confirm
   * <template>
   *   <v-app>
   *     ...
   *     <confirm ref="confirm"></confirm>
   *   </v-app>
   * </template>
   *
   * mounted() {
   *   this.$root.$confirm = this.$refs.confirm.open
   * }
   * Example of using
    async confirmBooking(){
        if(await this.$root.$message('อนุมัติการจอง',['Are you sure?','second line'],{type:'alert'})){
            console.log('confirm')
            }else{
            console.log('reject')
            }
    }
   */
  export default {
    data: () => ({
      dialog: false,
      title: '',
      message: '',
      options: {
        type: 'confirm', // 'confirm' | 'alert'
        color: 'primary',
        width: 400
      }
    }),
    watch: {
      dialog(val) {
        if (val) {
          this.$nextTick(() => {
            setTimeout(() => {
              this.focusButton();
            }, 100); // Small delay to ensure DOM is ready
          });
        } else {
          // Vuetify closes this dialog on its own when the overlay is clicked or
          // Esc is pressed, without going through close(). Callers `await` open(),
          // so a promise left unsettled here hangs them forever -- and everything
          // after their await, such as clearing a rejected zipcode, never runs.
          // Dismissing means "no": alerts resolve undefined, confirms resolve false.
          this.settle(this.options.type === 'alert' ? undefined : false);
        }
      }
    },
    methods: {
      open(title, message, options = {}) {
        this.dialog = true;
        this.title = title;
        this.message = message;
        this.options = { ...this.options, ...options };
        return new Promise((resolve) => {
          this.resolve = resolve;
        });
      },
      close(value) {
        this.settle(value);
        this.dialog = false;
      },
      // Resolves once and once only, so the button that closed the dialog wins
      // over the watcher that fires as it disappears.
      settle(value) {
        if (!this.resolve) return;
        const done = this.resolve;
        this.resolve = null;
        done(value);
      },
      handleEnter() {
        if (this.options.type === 'alert') {
          this.close();
        } else {
          this.close(true);
        }
      },
      focusButton() {
        try {
          const buttonRef = this.options.type === 'alert' 
            ? this.$refs.okButton 
            : this.$refs.confirmButton;
          
          if (buttonRef && buttonRef.$el) {
            buttonRef.$el.focus();
          }
        } catch (e) {
          // Focus is a convenience; a dialog without it still works.
        }
      },
    }
  };
  </script>